import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Send, ChevronDown, X, Wrench, CheckCircle2, RotateCcw } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import MachineIdentity, { MachineMark } from "@/components/maintenance/MachineMark";
import { agoFrom } from "@/components/maintenance/lib";

// Requests & pending points — the small asks and known niggles that aren't
// breakdowns. Production raises them (same hands, same permission as a
// breakdown report); maintenance takes them up and resolves them. Nothing is
// deleted and only an admin can reopen something already resolved.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND = {
  request:       { label: "Request",       hint: "an ask — “brighter task light on the setup bench”" },
  pending_point: { label: "Pending point", hint: "a known small defect — “loose guard latch”" },
};

function KindChip({ kind }) {
  const k = KIND[kind] || KIND.request;
  return (
    <span className="rl-stage-pill shrink-0" style={{
      color: kind === "pending_point" ? "#92600a" : "#2F6DAE",
      borderColor: kind === "pending_point" ? "rgba(146,96,10,0.35)" : "rgba(47,109,174,0.35)",
      background: kind === "pending_point" ? "rgba(146,96,10,0.08)" : "rgba(47,109,174,0.08)",
    }}>
      {k.label}
    </span>
  );
}

// A simple searchable machine list — the lightbox's tile-search, shrunk into a
// popover. Optional: most requests aren't about one machine.
function MachinePicker({ machines, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const picked = useMemo(() => (machines || []).find((m) => m.machine_no === value) || null, [machines, value]);

  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (machines || []).filter((m) => !q || `${m.name} ${m.machine_no} ${m.make}`.toLowerCase().includes(q)).slice(0, 40);
  }, [machines, query]);

  if (picked) {
    return (
      <span className="inline-flex items-center gap-2 h-9 pl-2 pr-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]" data-testid="mt-rq-machine-picked">
        <MachineMark make={picked.make} size={20} />
        <span className="text-[12.5px] font-semibold truncate max-w-[150px]">{picked.name}</span>
        <span className="rl-me-chip rl-carved">{picked.machine_no}</span>
        <button onClick={() => onChange(null)} aria-label="Clear machine" title="Clear machine"
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
          <X className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  return (
    <span className="relative" ref={boxRef}>
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Machine (optional)…"
        data-testid="mt-rq-machine-search"
        className="h-9 w-[190px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
      />
      {open && rows.length > 0 && (
        <div className="absolute z-30 mt-1 w-[260px] max-h-[240px] overflow-y-auto rl-frost rounded-xl border border-[hsl(var(--border))] p-1">
          {rows.map((m) => (
            <button
              key={m.machine_no}
              onClick={() => { onChange(m.machine_no); setOpen(false); setQuery(""); }}
              data-testid={`mt-rq-machine-${m.machine_no}`}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[hsl(var(--muted))]/50 text-left"
            >
              <MachineMark make={m.make} size={20} />
              <span className="text-[12.5px] font-semibold truncate">{m.name}</span>
              <span className="rl-me-chip rl-carved ml-auto">{m.machine_no}</span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function RaiseCard({ machines, canRaise, prefillMachine, autoFocus, onRaised }) {
  const [kind, setKind] = useState("request");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [machineNo, setMachineNo] = useState(prefillMachine || null);
  const [busy, setBusy] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => { if (prefillMachine) setMachineNo(prefillMachine); }, [prefillMachine]);
  useEffect(() => { if (autoFocus) setTimeout(() => titleRef.current?.focus(), 120); }, [autoFocus]);

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/requests`, {
        kind, title: title.trim(), note: note.trim(), machine_no: machineNo || null,
      });
      toast.success(kind === "pending_point" ? "Pending point noted" : "Request raised");
      setTitle(""); setNote(""); setMachineNo(null);
      onRaised();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through.");
    } finally { setBusy(false); }
  };

  if (!canRaise) {
    return (
      <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] px-[18px] py-3.5 mb-4 text-[12px] text-[hsl(var(--muted-foreground))]" data-testid="mt-rq-raise-blocked">
        Production raises requests and pending points — you can follow them here.
      </div>
    );
  }

  return (
    <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] px-[18px] py-4 mb-4" data-testid="mt-rq-raise">
      <div className="flex gap-2 mb-3">
        {Object.entries(KIND).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            data-testid={`mt-rq-kind-${k}`}
            className={`rl-prob-chip rl-pill ${kind === k ? "is-picked" : ""}`}
          >
            {v.label}
          </button>
        ))}
        <span className="self-center text-[11px] text-[hsl(var(--muted-foreground))] hidden sm:inline">{KIND[kind].hint}</span>
      </div>
      <div className="flex gap-2 flex-wrap items-start">
        <input
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder={kind === "pending_point" ? "Loose guard latch on the door…" : "Brighter task light on the setup bench…"}
          data-testid="mt-rq-title"
          className="h-9 flex-1 min-w-[240px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
        />
        <MachinePicker machines={machines} value={machineNo} onChange={setMachineNo} />
        <button
          onClick={submit}
          disabled={!title.trim() || busy}
          title={title.trim() ? undefined : "Say what you need first"}
          data-testid="mt-rq-submit"
          className="rl-lit-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[hsl(var(--primary))] text-white text-[12.5px] font-bold disabled:opacity-45 disabled:cursor-not-allowed"
        >
          <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Raise it"}
        </button>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        placeholder="Anything else worth knowing? (optional)"
        data-testid="mt-rq-note"
        className="mt-2 h-9 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[12.5px] outline-none focus:border-[hsl(var(--primary))]/60"
      />
      <p className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-2">Raised by you automatically — no name to type.</p>
    </div>
  );
}

function Row({ q, can, busy, onMove, nowMs }) {
  const resolved = q.status === "resolved";
  return (
    <div className="flex gap-3 py-3 border-t border-[hsl(var(--border))]/60 items-start" data-testid={`mt-rq-row-${q.id}`}>
      <KindChip kind={q.kind} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-[650] leading-[1.35]">{q.title}</div>
        {q.note && <div className="text-[12px] text-[hsl(var(--muted-foreground))] mt-[3px] leading-[1.45]">{q.note}</div>}
        {q.machine && <div className="mt-1.5"><MachineIdentity machine={q.machine} size={22} showCategory={false} /></div>}
        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-1.5">
          {q.id} · raised by {q.raised_by?.name || "—"} · {agoFrom(q.raised_at, nowMs)}
          {resolved && q.resolved_by ? ` · resolved by ${q.resolved_by.name}` : ""}
        </div>
        {resolved && q.resolution_note && (
          <div className="text-[11.5px] rl-tx-green mt-1">“{q.resolution_note}”</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {q.status === "open" && (can.can_repair_machines ? (
          <button
            onClick={() => onMove(q, "in_progress")}
            disabled={busy}
            data-testid={`mt-rq-take-${q.id}`}
            className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
          >
            <Wrench className="w-3.5 h-3.5" /> Take it up
          </button>
        ) : (
          <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic" title="Only the maintenance team works requests">waiting for maintenance</span>
        ))}
        {q.status === "in_progress" && (can.can_repair_machines ? (
          <button
            onClick={() => onMove(q, "resolved")}
            disabled={busy}
            data-testid={`mt-rq-resolve-${q.id}`}
            className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12px] font-bold disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
          </button>
        ) : (
          <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic" title="Only the maintenance team works requests">maintenance is on it</span>
        ))}
        {resolved && can.can_admin_maintenance && (
          <button
            onClick={() => onMove(q, "open")}
            disabled={busy}
            data-testid={`mt-rq-reopen-${q.id}`}
            className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reopen
          </button>
        )}
      </div>
    </div>
  );
}

export default function MaintenanceRequestsPage() {
  const location = useLocation();
  const { can = {}, refresh, refreshKey } = useMaintenance() || {};
  const [data, setData] = useState(null);
  const [machines, setMachines] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [resolving, setResolving] = useState(null);   // {q} — the resolution note
  const [resolveNote, setResolveNote] = useState("");

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/maintenance/requests`);
      setData(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load requests.");
      setData({ open: [], in_progress: [], resolved: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    axios.get(`${API}/maintenance/machines`).then((r) => setMachines(r.data?.machines || [])).catch(() => {});
  }, []);

  const post = useCallback(async (q, status, resolution_note) => {
    setBusyId(q.id);
    try {
      await axios.post(`${API}/maintenance/requests/${q.id}/status`, { status, resolution_note: resolution_note || "" });
      toast.success(status === "resolved" ? "Resolved" : status === "open" ? "Reopened" : "Taken up");
      setResolving(null); setResolveNote("");
      await load();
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't go through.");
    } finally { setBusyId(null); }
  }, [load, refresh]);

  // Resolving asks for an optional line first — one small inline step, no dialog.
  const move = useCallback((q, status) => {
    if (status === "resolved") { setResolving(q); setResolveNote(""); return; }
    post(q, status);
  }, [post]);

  const open = data?.open || [];
  const inProgress = data?.in_progress || [];
  const resolved = data?.resolved || [];
  const nothing = data && open.length === 0 && inProgress.length === 0 && resolved.length === 0;

  const Group = ({ title, count, children }) => (
    <div className="px-[18px] py-2">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))] pt-2">
        {title} <span className="font-mono-data">{count}</span>
      </div>
      {children}
    </div>
  );

  return (
    <>
      <RaiseCard
        machines={machines}
        canRaise={!!can.can_report_breakdown}
        prefillMachine={location.state?.machineNo}
        autoFocus={!!location.state?.focus}
        onRaised={() => { load(); refresh?.(); }}
      />

      <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] overflow-hidden" data-testid="mt-requests-page">
        <div className="px-[18px] pt-4 pb-3 border-b border-[hsl(var(--border))]">
          <div className="text-[17px] font-extrabold flex items-center gap-2.5" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
            <ClipboardList className="w-[18px] h-[18px] text-[#2F6DAE]" />
            Requests &amp; Pending Points
          </div>
          <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">
            Anything that isn't a breakdown but shouldn't be forgotten
          </div>
        </div>

        {data === null && <p className="px-6 py-10 text-center text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</p>}

        {nothing && (
          <p className="px-6 py-12 text-center text-[13px] text-[hsl(var(--muted-foreground))] max-w-[460px] mx-auto leading-relaxed">
            Small asks and niggles land here — anything that isn't a breakdown but shouldn't be forgotten.
          </p>
        )}

        {data && !nothing && (
          <>
            {open.length > 0 && (
              <Group title="Open" count={open.length}>
                {open.map((q) => <Row key={q.id} q={q} can={can} busy={busyId === q.id} onMove={move} nowMs={nowMs} />)}
              </Group>
            )}
            {inProgress.length > 0 && (
              <Group title="In progress" count={inProgress.length}>
                {inProgress.map((q) => <Row key={q.id} q={q} can={can} busy={busyId === q.id} onMove={move} nowMs={nowMs} />)}
              </Group>
            )}
            {data.resolved_total > 0 && (
              <div className="px-[18px] py-2 border-t border-[hsl(var(--border))]">
                <button
                  onClick={() => setShowResolved((v) => !v)}
                  data-testid="mt-rq-resolved-toggle"
                  className="flex items-center gap-2 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]"
                >
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showResolved ? "" : "-rotate-90"}`} />
                  Resolved <span className="font-mono-data">{data.resolved_total}</span>
                  <span className="font-medium normal-case tracking-normal text-[10.5px]">
                    {data.resolved_total > resolved.length ? `· last ${resolved.length} shown` : ""}
                  </span>
                </button>
                {showResolved && resolved.map((q) => (
                  <Row key={q.id} q={q} can={can} busy={busyId === q.id} onMove={move} nowMs={nowMs} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Resolving asks for one optional line — kept inline so the board never
          jumps to a dialog for a two-word answer. */}
      {resolving && (
        <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] px-[18px] py-4 mt-4" data-testid="mt-rq-resolve-card">
          <div className="text-[12px] font-semibold mb-2">Resolve “{resolving.title}”</div>
          <div className="flex gap-2 flex-wrap">
            <input
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); post(resolving, "resolved", resolveNote.trim()); }
                if (e.key === "Escape") { setResolving(null); }
              }}
              autoFocus
              placeholder="What was done? (optional)"
              data-testid="mt-rq-resolve-note"
              className="h-9 flex-1 min-w-[220px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 text-[13px] outline-none focus:border-[hsl(var(--primary))]/60"
            />
            <button
              onClick={() => post(resolving, "resolved", resolveNote.trim())}
              disabled={busyId === resolving.id}
              data-testid="mt-rq-resolve-confirm"
              className="rl-lit-btn inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-green-600 hover:bg-green-500 text-white text-[12.5px] font-bold disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
            </button>
            <button
              onClick={() => setResolving(null)}
              className="rl-pill h-9 px-4 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12.5px] font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
